import React, { useEffect, useState } from "react";

import { Button } from "../ui/button";
import Input from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { todayISODate } from "../../lib/utils";

export interface AmountEntryValues<C extends string> {
  category: C;
  description?: string;
  amount: number;
  expense_date: string;
}

interface AmountEntryDialogProps<C extends string> {
  open: boolean;
  title: string;
  description?: string;
  categoryLabels: Record<C, string>;
  defaultCategory: C;
  initialValues?: AmountEntryValues<C> | null;
  isSubmitting?: boolean;
  onSubmit: (values: AmountEntryValues<C>) => void;
  onClose: () => void;
}

function AmountEntryDialog<C extends string>({
  open,
  title,
  description,
  categoryLabels,
  defaultCategory,
  initialValues,
  isSubmitting,
  onSubmit,
  onClose,
}: AmountEntryDialogProps<C>) {
  const [category, setCategory] = useState<C>(defaultCategory);
  const [amount, setAmount] = useState("");
  const [expenseDate, setExpenseDate] = useState(todayISODate());
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!open) return;
    setCategory(initialValues?.category ?? defaultCategory);
    setAmount(initialValues ? String(initialValues.amount) : "");
    setExpenseDate(initialValues?.expense_date ?? todayISODate());
    setNotes(initialValues?.description ?? "");
  }, [open, initialValues, defaultCategory]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const parsedAmount = Math.round(parseFloat(amount) * 100) / 100;
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) return;

    onSubmit({
      category,
      amount: parsedAmount,
      expense_date: expenseDate,
      description: notes.trim() || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Category *</Label>
            <Select value={category} onValueChange={(value) => setCategory(value as C)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(categoryLabels) as C[]).map((key) => (
                  <SelectItem key={key} value={key}>
                    {categoryLabels[key]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Amount (RM) *</Label>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="500.00"
                required
              />
            </div>
            <div>
              <Label>Date *</Label>
              <Input
                type="date"
                value={expenseDate}
                max={todayISODate()}
                onChange={(e) => setExpenseDate(e.target.value)}
                required
              />
            </div>
          </div>

          <div>
            <Label>Description</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes"
              maxLength={500}
              rows={3}
            />
          </div>

          <div className="flex space-x-2 pt-2">
            <Button type="submit" className="flex-1" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default AmountEntryDialog;
